import c from 'classnames';
import Modal from 'core/components/Modal';
import { ieFix } from 'core/style';
import injectSheet, { createStyles } from 'core/style/injectSheet';
import { ITheme, WithStyles } from 'core/style/interfaces';
import { addTimeSpanToDate, formatToLocalDayMonth, formatToLocalTime, isHtmlEmpty, isImpossibleDate } from 'core/utils';
import { nearestSeminarsAndStarted, nearestWebinarsAndStarted } from 'core/utils/product-forms/filterProductForms';
import React from 'react';
import PayButtonContainer from 'site/components/payments/PayButtonContainer';
import { IPayProductForm } from 'site/components/payments/PayButtonPresent';
import { Product, ProductForm, ProductFormPublicTypeEnum, Seminar, Webinar } from 'site/graphql';

export type IProductModalProductForm = IPayProductForm &
    Pick<ProductForm, 'description' | 'type'> & {
        product: Pick<Product, 'name'>;
        webinars: Pick<Webinar, 'id' | 'date' | 'hours' | 'title'>[];
        seminars: Pick<Seminar, 'id' | 'date' | 'hours' | 'title'>[];
    };
export type IProductModalProduct = Pick<Product, 'id' | 'name'>;

type IProductModalProps = {
    productForms: IProductModalProductForm[];
    selectedProductFormIndex: number;
    onClose: () => void;
    richWarnings: string;
    isOpen: boolean;
    product: IProductModalProduct;
} & WithStyles<typeof styles>;

type IProductModalState = {
    tab: 'program' | 'schedule';
};

class ProductModal extends React.Component<IProductModalProps, IProductModalState> {
    constructor(props) {
        super(props);

        this.state = {
            tab: isHtmlEmpty(props.richWarnings) ? 'schedule' : 'program',
        };
    }

    handleChangeTab = (tab: IProductModalState['tab']) => this.setState({ tab });

    renderSheduleProductForm = (productForm: IProductModalProductForm) => {
        const { classes } = this.props;
        const startDate = (productForm.webinars[0] || productForm.seminars[0]).date;

        type IFormItem = IProductModalProductForm['webinars'][0] | IProductModalProductForm['seminars'][0];
        const formItems: IFormItem[] = productForm.webinars.length > 0 ? productForm.webinars : productForm.seminars;

        return (
            <React.Fragment key={productForm.id}>
                <div className={classes.title}>
                    {productForm.title}. {productForm.product.name}. Начало {formatToLocalDayMonth(startDate)}
                </div>
                {productForm.description && (
                    <div className={classes.productFormDescription}>{productForm.description}</div>
                )}
                {formItems.map((fi, i) => {
                    const date = isImpossibleDate(fi.date)
                        ? 'Скоро'
                        : `${formatToLocalDayMonth(fi.date)} с ${formatToLocalTime(fi.date)} до ${formatToLocalTime(
                              addTimeSpanToDate(fi.date, fi.hours),
                          )}`;

                    return (
                        <div key={fi.id} className={classes.formItemContainer}>
                            <div className={classes.numeralFormItem}>{i + 1}</div>
                            <div className={classes.formItemText}>
                                {fi.title}. {date}
                            </div>
                        </div>
                    );
                })}
            </React.Fragment>
        );
    };

    renderTab(tabName: IProductModalState['tab'], label: string) {
        const { classes } = this.props;
        const { tab } = this.state;

        return (
            <div
                className={c(classes.tab, { [classes.tabSelected]: tab === tabName })}
                onClick={() => this.handleChangeTab(tabName)}
            >
                {label}
            </div>
        );
    }

    render() {
        const { classes, isOpen, onClose, richWarnings, productForms, selectedProductFormIndex, product } = this.props;
        const { tab } = this.state;
        const selectedProductForm = productForms[selectedProductFormIndex];
        const filteredProductForms = [
            ...nearestWebinarsAndStarted(productForms),
            ...nearestSeminarsAndStarted(productForms),
        ];

        return (
            <Modal label="Подробнее о занятии" open={isOpen} onClose={onClose} zIndex={99}>
                <div className={classes.container}>
                    <div className={classes.tabsContainer}>
                        {!isHtmlEmpty(richWarnings) && this.renderTab('program', 'Программа')}
                        {productForms.some(pf =>
                            [ProductFormPublicTypeEnum.Webinar, ProductFormPublicTypeEnum.Seminar].includes(pf.type),
                        ) && this.renderTab('schedule', 'Расписание')}
                    </div>

                    {tab === 'program' ? (
                        <div className={classes.contentContainer}>
                            <div className={classes.title}>Программа обучения &quot;{product.name}&quot;</div>
                            <div className={classes.warnings} dangerouslySetInnerHTML={{ __html: richWarnings }} />
                        </div>
                    ) : (
                        <div className={classes.contentContainer}>
                            {filteredProductForms.map(this.renderSheduleProductForm)}
                        </div>
                    )}

                    {selectedProductForm && (
                        <div className={classes.buttonContainer}>
                            <PayButtonContainer productForm={selectedProductForm} />
                        </div>
                    )}
                </div>
            </Modal>
        );
    }
}

const styles = (theme: ITheme) =>
    createStyles({
        container: {
            backgroundColor: 'white',
            padding: 1 + 'rem',
            width: 90 + 'vw',
            maxHeight: 80 + 'vh',
            display: 'flex',
            flexDirection: 'column',

            ...ieFix({ display: 'block' }),

            [theme.breakpoints.down('sm')]: {
                width: 'calc(100vw - 2rem)', // весь экран - 2*padding
                height: 'calc(100vh - 2rem)', // весь экран - 2*padding
                justifyContent: 'center',
                maxHeight: 'none',
            },
        },
        tabsContainer: {
            display: 'flex',
            marginBottom: 2 + 'rem',
        },
        tab: {
            fontSize: 1.5 + 'rem',
            fontWeight: 'bold',
            marginRight: 2 + 'rem',
            cursor: 'pointer',

            '&:hover': {
                color: theme.colors.accent,
            },

            [theme.breakpoints.down('sm')]: {
                fontSize: 1.2 + 'rem',
            },
        },
        tabSelected: {
            color: theme.colors.accent,
            position: 'relative',

            '&:after': {
                content: '""',
                borderBottom: `5px solid ${theme.colors.accent}`,
                position: 'absolute',
                width: 100 + '%',
                bottom: -10,
                right: 0,
            },
        },
        contentContainer: {
            maxHeight: 60 + 'vh',
            overflow: 'auto',

            [theme.breakpoints.down('sm')]: {
                maxHeight: 50 + 'vh',
            },
        },
        buttonContainer: {
            width: 25 + '%',
            margin: '0 auto',
            marginTop: 1 + 'rem',

            [theme.breakpoints.down('sm')]: {
                width: 100 + '%',
            },
        },
        title: {
            fontSize: 1.4 + 'rem',
            fontWeight: 'bold',
        },
        formItemContainer: {
            display: 'flex',
            padding: '1rem 0',
            alignItems: 'center',

            [theme.breakpoints.down('sm')]: {
                flexDirection: 'column',
            },
        },
        formItemText: {
            marginLeft: 1 + 'rem',
        },
        numeralFormItem: {
            width: 35,
            height: 35,
            lineHeight: '35px',
            borderRadius: 50 + '%',
            backgroundColor: theme.colors.accent,
            color: 'white',
            textAlign: 'center',
        },
        warnings: {
            padding: '1rem 0',
        },
        productFormDescription: {
            marginTop: 1 + 'rem',
        },
    });

export default injectSheet(styles, 'ProductModal')(ProductModal);
